import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { FaVolumeUp, FaVolumeMute, FaPlay } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { AppDispatch } from '../../redux/Store';
import { updateSoundPreferences, fetchSoundPreferences } from '../../redux/thunks/userPreferencesThunks';
import soundManager, { NotificationSoundType } from '../../utils/SoundManager';
import {
  SettingsPanel,
  SettingsRow,
  SettingsToggle,
  SettingsSaveButton,
  SettingsNote,
  SettingsSectionHeading,
  SettingsSaveStatus,
} from './primitives';
import './SoundSettings.scss';

export const SoundSettings: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  const [enabled, setEnabled] = useState(true);
  const [volume, setVolume] = useState(0.7);
  const [soundTypes, setSoundTypes] = useState({
    message: true,
    like: true,
    comment: true,
    follow: true,
    mention: true,
    system: false,
    purchase: true,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState<SettingsSaveStatus>('idle');

  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      const preferences = await dispatch(fetchSoundPreferences()).unwrap();
      if (preferences) {
        setEnabled(preferences.enabled);
        setVolume(preferences.volume);
        setSoundTypes(preferences.types);
        soundManager.updatePreferences(preferences);
      }
    } catch (error) {
      console.log('Using default sound preferences');
    }
  };

  const handleSave = async () => {
    setIsLoading(true);
    setSaveStatus('saving');

    const preferences = { enabled, volume, types: soundTypes };

    try {
      await dispatch(updateSoundPreferences(preferences)).unwrap();
      soundManager.updatePreferences(preferences);
      setSaveStatus('saved');
      toast.success('Sound preferences saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (error) {
      console.error('Failed to save sound preferences:', error);
      setSaveStatus('error');
      toast.error('Failed to save sound preferences');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEnabledToggle = (next: boolean) => {
    setEnabled(next);
    if (next) soundManager.unmute();
    else soundManager.mute();
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    soundManager.setVolume(newVolume);
  };

  const handleTypeToggle = (type: NotificationSoundType) => {
    setSoundTypes((prev) => ({ ...prev, [type]: !prev[type] }));
  };

  const handleTestSound = async (type: NotificationSoundType) => {
    await soundManager.test(type);
  };

  const soundTypeLabels: Record<NotificationSoundType, string> = {
    message: 'New Messages',
    like: 'Likes',
    comment: 'Comments',
    follow: 'New Followers',
    mention: 'Mentions',
    system: 'System Notifications',
    purchase: 'Profile Purchases',
  };

  const soundTypeDescriptions: Record<NotificationSoundType, string> = {
    message: 'Play sound when you receive new direct messages',
    like: 'Play sound when someone likes your post or profile',
    comment: 'Play sound when someone comments on your content',
    follow: 'Play sound when someone follows you',
    mention: 'Play sound when someone mentions you',
    system: 'Play sound for system updates and announcements',
    purchase: 'Play sound when someone purchases your profile',
  };

  return (
    <SettingsPanel
      title="Notification Sounds"
      description="Customize sound notifications for different events"
    >
      <SettingsRow
        icon={enabled ? <FaVolumeUp /> : <FaVolumeMute />}
        title="Enable Notification Sounds"
        description="Turn all notification sounds on or off"
        control={
          <SettingsToggle
            checked={enabled}
            onChange={handleEnabledToggle}
            ariaLabel="Enable notification sounds"
          />
        }
      />

      {enabled && (
        <SettingsRow
          title="Volume"
          description="Adjust notification sound volume"
        >
          <div className="sound-settings-volume">
            <FaVolumeMute className="sound-settings-volume__icon" />
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={volume}
              onChange={handleVolumeChange}
              className="sound-settings-volume__slider"
              aria-label="Sound volume"
            />
            <FaVolumeUp className="sound-settings-volume__icon" />
            <span className="sound-settings-volume__value">{Math.round(volume * 100)}%</span>
          </div>
        </SettingsRow>
      )}

      {enabled && (
        <>
          <SettingsSectionHeading title="Sound types" />
          {(Object.keys(soundTypes) as NotificationSoundType[]).map((type) => (
            <SettingsRow
              key={type}
              title={soundTypeLabels[type]}
              description={soundTypeDescriptions[type]}
              control={
                <>
                  <button
                    type="button"
                    className="sound-settings-test-btn"
                    onClick={() => handleTestSound(type)}
                    title="Test sound"
                    aria-label={`Test ${soundTypeLabels[type]} sound`}
                  >
                    <FaPlay />
                  </button>
                  <SettingsToggle
                    checked={soundTypes[type]}
                    onChange={() => handleTypeToggle(type)}
                    ariaLabel={`Toggle ${soundTypeLabels[type]}`}
                  />
                </>
              }
            />
          ))}
        </>
      )}

      <SettingsSaveButton
        status={saveStatus}
        onClick={handleSave}
        disabled={isLoading}
        idleLabel="Save Preferences"
      />

      <SettingsNote tone="info">
        <p>
          <strong>Note:</strong> Sound notifications require browser permission. If sounds don't play,
          check your browser's autoplay settings and ensure you've interacted with the page.
        </p>
      </SettingsNote>
    </SettingsPanel>
  );
};

export default SoundSettings;
